/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { isDebugContext } from "../IsDebugContext";
import "../FormRender.scss";
import { HtmlAttributes } from "../Constants/HtmlAttributes";
import { CssUtils } from "../CssUtils";
import { FeedbackType } from "./FeedbackType";
import { capitalizeFirstLetter, T } from "../Localization";
import { FormRenderingStatus, FormRenderingStatusIds, FormSubmitStatus } from "../Constants/FormStatus";
import { FormUtility } from "../FormUtility";
import { ErrorFeedback, EventAtCapacityOnLoad, EventAtCapacitySubmissionError, EventNotLive, EventRegistrationAlreadyCanceled, EventNotLiveSubmissionError, NoSessionsFoundSubmissionError, ReloadFeedback, SessionRegistrationRequiredSubmissionError, SessionValidationFailedSubmissionError, SingleSessionRegistrationRequiredSubmissionError, SessionCapacityIsFullSubmissionError, EventCancelationLoadError } from "./Feedback";
import { EventFormFeedback } from "../EventManagement/EventFormFeedback";
var dialogProviderContainerClassName = "dialogProviderContainer";
export var defaultContainerSize = { height: 400, width: 600 };
export var showNotificationBar = function (notificationName, formId) { return __awaiter(void 0, void 0, void 0, function () {
    var notificationSelector, notificationElement, messageContainer, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                notificationSelector = "[".concat(HtmlAttributes.formId, "='").concat(formId, "'] .notification-message[").concat(HtmlAttributes.dataNotificationName, "='").concat(notificationName, "']");
                notificationElement = document.querySelector(notificationSelector);
                if (!(notificationElement != null)) return [3 /*break*/, 2];
                messageContainer = document.createElement("div");
                _a = messageContainer;
                return [4 /*yield*/, T(capitalizeFirstLetter(notificationName))];
            case 1:
                _a.textContent = _b.sent();
                notificationElement.replaceChildren(messageContainer);
                CssUtils.removeClass(notificationElement, "notification-message-hide");
                _b.label = 2;
            case 2: return [2 /*return*/];
        }
    });
}); };
export var showRenderingFeedback = function (formRenderingStatus, form) {
    switch (formRenderingStatus) {
        case FormRenderingStatus.EventCapacityIsFull:
            showFeedbackWithEventInformation(EventAtCapacityOnLoad, form);
            break;
        case FormRenderingStatus.EventWaitlisted:
            showNotificationBar(FormRenderingStatusIds.EventWaitlisted, form.FormId);
            break;
        case FormRenderingStatus.EventNotLive:
        case FormRenderingStatus.FormNotLive:
            showFeedback(EventNotLive, form);
            break;
        case FormRenderingStatus.InternalServerError:
            showFeedback(__assign(__assign({}, ReloadFeedback), { message: "FormFailedToLoad" }), form);
            break;
        case FormRenderingStatus.NotFound:
            if (FormUtility.isEventCancelationForm()) {
                showFeedback(EventCancelationLoadError, form, undefined, undefined, false, {
                    containerClass: "eventCancelationLoadError",
                    useReloadButton: true
                });
            }
            else {
                showFeedback(__assign(__assign({}, ErrorFeedback), { message: "FormFailedToLoad" }), form);
            }
            break;
        case FormRenderingStatus.EventRegistrationAlreadyCanceled:
            showFeedback(EventRegistrationAlreadyCanceled, form);
            break;
        case FormRenderingStatus.SuccessWithSessions:
            EventFormFeedback.showSessionsBlocks(form);
            break;
        case FormRenderingStatus.Success:
            showNotificationBar(getFormSuccessInformationStatus(form), form.FormId);
            break;
        default:
            break;
    }
};
export var showSubmissionFeedback = function (formSubmissionStatus, form) {
    var height = form.offsetHeight;
    var width = form.offsetWidth;
    if (isFeedbackType(formSubmissionStatus)) {
        if (FormUtility.isEventCancelationForm()) {
            showFeedback(formSubmissionStatus, form, height, width, true);
        }
        else {
            showFeedback(formSubmissionStatus, form, height, width);
        }
    }
    else {
        switch (formSubmissionStatus) {
            case FormSubmitStatus.EventCapacityIsFull:
                showFeedback(EventAtCapacitySubmissionError, form, height, width);
                break;
            case FormSubmitStatus.EventNotLive:
                showFeedback(EventNotLiveSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.NoSessionsFound:
                showFeedback(NoSessionsFoundSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.SessionRegistrationRequired:
                showFeedback(SessionRegistrationRequiredSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.SessionValidationFailed:
                showFeedback(SessionValidationFailedSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.SingleSessionRegistrationRequired:
                showFeedback(SingleSessionRegistrationRequiredSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.SessionCapacityIsFull:
                showFeedback(SessionCapacityIsFullSubmissionError, form, height, width);
                break;
            case FormSubmitStatus.ValidationError:
            case FormSubmitStatus.InternalServerError:
            default:
                showFeedback(ErrorFeedback, form, height, width);
                break;
        }
    }
};
export var showFeedbackWithEventInformation = function (feedback, form, containerHeight, containerWidth) {
    if (containerHeight === void 0) { containerHeight = defaultContainerSize.height; }
    if (containerWidth === void 0) { containerWidth = defaultContainerSize.width; }
    return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, showFeedback(feedback, form, containerHeight, containerWidth, true)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
};
export var showFeedback = function (feedback, form, containerHeight, containerWidth, includeEventInformation, extraParams) {
    if (containerHeight === void 0) { containerHeight = defaultContainerSize.height; }
    if (containerWidth === void 0) { containerWidth = defaultContainerSize.width; }
    if (includeEventInformation === void 0) { includeEventInformation = false; }
    return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (FormUtility.isForm(form)) {
                form.FormPlaceholders.forEach(function (placeholder) { return __awaiter(void 0, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        if (isAnyErrorMessageShown(placeholder.Element)) {
                            return [2 /*return*/];
                        }
                        showFeedbackFullScreen(feedback, placeholder.Element, containerHeight, containerWidth, form.FeedbackPosition, includeEventInformation, extraParams);
                        return [2 /*return*/];
                    });
                }); });
            }
            else {
                showFeedbackFullScreen(feedback, form, containerHeight, containerWidth, undefined, includeEventInformation, extraParams);
            }
            return [2 /*return*/];
        });
    });
};
export var showFeedbackFullScreen = function (feedback, formElement, containerHeight, containerWidth, feedbackPosition, includeEventInformation, extraParams) { return __awaiter(void 0, void 0, void 0, function () {
    var _a, containerClass, useReloadButton, formContainer, container, internalContainer, messageContainer, iconContainer, _b, eventInformationOnForm, sessionDetails, isEventCancelationForm, linkContainer, buttonElement, _c, linkElement, _d, form;
    return __generator(this, function (_e) {
        switch (_e.label) {
            case 0:
                _a = extraParams || { useButton: false }, containerClass = _a.containerClass, useReloadButton = _a.useReloadButton;
                formContainer = formElement instanceof HTMLFormElement ? formElement.parentElement : formElement;
                container = document.createElement("div");
                internalContainer = document.createElement("div");
                messageContainer = document.createElement("div");
                iconContainer = document.createElement("img");
                fixContainerSize(containerHeight, containerWidth, container);
                container.className = "onFormSubmittedFeedback ".concat(containerClass ? "".concat(containerClass) : "");
                if (feedback.type == FeedbackType.Error) {
                    internalContainer.setAttribute("data-submissionresponse", "error");
                    setImageSource(iconContainer, feedback.image);
                }
                else {
                    internalContainer.setAttribute("data-submissionresponse", "success");
                    setImageSource(iconContainer, feedback.image);
                }
                iconContainer.className = "onFormSubmittedFeedbackIcon";
                _b = messageContainer;
                return [4 /*yield*/, T(feedback.message)];
            case 1:
                _b.textContent = _e.sent();
                messageContainer.className = "onFormSubmittedFeedbackMessage";
                internalContainer.className = "onFormSubmittedFeedbackInternalContainer";
                if (includeEventInformation) {
                    eventInformationOnForm = formContainer.querySelector("div[".concat(HtmlAttributes.editorBlockType, "=\"About\"]"));
                    if (eventInformationOnForm) {
                        internalContainer.appendChild(eventInformationOnForm);
                    }
                }
                internalContainer.appendChild(iconContainer);
                internalContainer.appendChild(messageContainer);
                if (!(feedback.type === FeedbackType.Success)) return [3 /*break*/, 5];
                return [4 /*yield*/, EventFormFeedback.getSessionSubmissionDetails(formElement)];
            case 2:
                sessionDetails = _e.sent();
                isEventCancelationForm = FormUtility.isEventCancelationForm();
                if (!isEventCancelationForm) return [3 /*break*/, 4];
                return [4 /*yield*/, EventFormFeedback.getCanceledSessionDetails(formElement)];
            case 3:
                sessionDetails = _e.sent();
                _e.label = 4;
            case 4:
                if (sessionDetails) {
                    internalContainer.appendChild(sessionDetails);
                }
                _e.label = 5;
            case 5:
                linkContainer = document.createElement("div");
                if (!useReloadButton) return [3 /*break*/, 7];
                buttonElement = document.createElement("button");
                _c = buttonElement;
                return [4 /*yield*/, T("ReloadButtonLabel")];
            case 6:
                _c.textContent = _e.sent();
                buttonElement.className = "dialogProviderReloadButton";
                buttonElement.onclick = function () {
                    window.location.reload();
                };
                linkContainer.className = "dialogProviderLinkContainer";
                linkContainer.appendChild(buttonElement);
                internalContainer.appendChild(linkContainer);
                return [3 /*break*/, 9];
            case 7:
                linkElement = document.createElement("a");
                if (!(feedback.link != null)) return [3 /*break*/, 9];
                linkElement.href = feedback.link.url;
                _d = linkElement;
                return [4 /*yield*/, T(feedback.link.name)];
            case 8:
                _d.textContent = _e.sent();
                linkContainer.className = "dialogProviderLinkContainer";
                linkContainer.appendChild(linkElement);
                internalContainer.appendChild(linkContainer);
                _e.label = 9;
            case 9:
                if (!feedback.render) return [3 /*break*/, 11];
                return [4 /*yield*/, feedback.render(internalContainer)];
            case 10:
                _e.sent();
                _e.label = 11;
            case 11:
                if (!feedbackPosition || feedbackPosition === "middle") {
                    internalContainer.style.top = (containerHeight - 400) / 2 + "px";
                }
                if (feedbackPosition === "top") {
                    internalContainer.style.top = "20px";
                }
                container.appendChild(internalContainer);
                formContainer.appendChild(container);
                form = formElement instanceof HTMLFormElement ? formElement : formElement.querySelector("form.marketingForm");
                form === null || form === void 0 ? void 0 : form.remove();
                return [2 /*return*/, container];
        }
    });
}); };
var fixContainerSize = function (containerHeight, containerWidth, container) {
    var minContainerSize = 400;
    if (containerHeight < minContainerSize) {
        container.style.height = minContainerSize + "px";
    }
    else if (containerHeight > window.innerHeight) {
        container.style.height = window.innerHeight + "px";
    }
    else {
        container.style.height = containerHeight + "px";
    }
    if (containerWidth < minContainerSize) {
        container.style.width = minContainerSize + "px";
    }
    else if (containerWidth > window.innerWidth) {
        container.style.width = window.innerWidth + "px";
    }
    else {
        container.style.width = containerWidth + "px";
    }
};
var setImageSource = function (image, value) {
    if (!isDebugContext()) {
        image.src = value;
    }
};
var isAnyErrorMessageShown = function (formPlaceholder) {
    return formPlaceholder.querySelector("div.".concat(dialogProviderContainerClassName)) != null;
};
var isFeedbackType = function (feedback) {
    return feedback.type != null;
};
var getFormSuccessInformationStatus = function (form) {
    return EventFormFeedback.getFormSuccessInformationStatus(form);
};
